/**
 * src/lib/api/voice-v2.js — Voice Intelligence v2 client API
 * Talks to /api/voice (GET for reads, POST for writes)
 * Uses the new capture/classify/action pipeline (v2 schema).
 */

import { apiFetch, API_BASE } from './client';
import { resolveDefaultProvider } from '../voice/sttProvider';
import { supabase as defaultSupabase } from '../supabase';
import { logVoiceEvent, newTraceId } from '../voice/voiceLogger';
// Large voice upload via MinIO presigned PUT (CEO 2026-05-31) — files larger
// than the free-plan Supabase 50 MB cap go straight to MinIO on the Hetzner box.
import { uploadViaPresigned } from './minioUpload';
// Resumable (S3-multipart) large-voice upload (TICKET-MINIO-RESUMABLE Slice 2) —
// used INSTEAD of the single presigned PUT when VITE_MINIO_RESUMABLE is on, so big
// mobile uploads survive interruptions (resume from the last completed part on
// reopen). The single-PUT path stays the default (proven on a real 188 MB file).
import { uploadResumable } from './resumableUpload';
// Resume-on-reopen persistence (TICKET-MINIO-RESUMABLE Slice 3b). Only touched on the
// resumable path (flag on) — persists multipart state + file bytes so an interrupted
// upload resumes on the next app open. The resumer that drains these lives in
// ./resumeUploadsOnStart.js (wired at app start, flag-gated).
import { saveUpload, patchUpload, deleteUpload } from './resumableUploadStore';

const VOICE = `${API_BASE}/ai`;

// ── Capture (L0) ────────────────────────────────────────────────────

/**
 * Resolve the user's STT provider preference (Phase 90-01).
 * Replaces getSttEngine() — silently migrates the legacy
 * `acn_stt_engine` localStorage key to `acn_stt_provider`.
 * Default: 'whisper'.
 */
export function getSttProviderName() {
  return resolveDefaultProvider();
}

/** @deprecated Use getSttProviderName(). Kept for backward compatibility. */
export function getSttEngine() {
  return resolveDefaultProvider();
}

// ── UVTP transcription path (Phase 2 of BCRUMB-UVTP migration) ────
//
// Sends audio bytes to /api/voice-transcribe (the unified pipeline).
// Returns ONLY the transcript text + metadata. Caller pipes the transcript
// into captureVoice() so the existing classifier/voice_records path runs
// unchanged — UVTP just replaces the transcription engine, nothing else.
//
// Why no `language` param: the 2026-05-02 Yaiza benchmark proved Whisper
// auto-detection beats voice_records.language hint (which is unreliable —
// it tags Spanish content as 'en' and triggers Whisper's translate mode,
// dropping similarity from 0.96 → 0.17). Always let Whisper auto-detect.
//
// Heavy path: when UVTP returns 202 with pollUrl, we poll until terminal.
// Polling cap defaults to 120s; configurable via maxPollMs.

const UVTP_ENDPOINT = `${API_BASE}/voice-transcribe`;
const UVTP_POLL_INTERVAL_MS_DEFAULT = 2000;
const UVTP_MAX_POLL_MS_DEFAULT = 120_000;
const UVTP_POST_TIMEOUT_MS = 60_000; // 60s — fast path is 4-6s on real audio, but Whisper can be slow under load

// Default fetch wrapper that injects Supabase auth headers + AbortController timeout.
// /api/voice-transcribe rejects unauthenticated requests with 401 — without this
// wrapper the worker would loop on auth failures and the breadcrumb sat in the
// queue for ~37s before being marked permanently failed (CEO live test 2026-05-04).
async function defaultUvtpFetch(url, options = {}) {
  const { getAuthHeaders } = await import('./client');
  const auth = await getAuthHeaders();
  const controller = new AbortController();
  const timeoutMs = typeof options.timeout === 'number' ? options.timeout : UVTP_POST_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // TICKET-1031: this function used to always pass `signal: controller.signal`,
  // which runs AFTER the `...options` spread and so silently overwrote any
  // caller-supplied signal (transcribeViaUvtp's wrappedFetch forwards one via
  // options.signal for conversation-mode cancel-on-close). Compose the two
  // instead so BOTH the timeout and a caller abort can cancel the request.
  const { signal: callerSignal, ...restOptions } = options;
  let signal = controller.signal;
  let onCallerAbort;
  if (callerSignal) {
    if (typeof AbortSignal?.any === 'function') {
      signal = AbortSignal.any([controller.signal, callerSignal]);
    } else {
      // ponytail: no AbortSignal.any on this runtime -- forward manually.
      onCallerAbort = () => controller.abort();
      callerSignal.addEventListener('abort', onCallerAbort);
    }
  }
  try {
    return await fetch(url, {
      ...restOptions,
      signal,
      headers: { ...(options.headers || {}), ...auth },
    });
  } finally {
    clearTimeout(timer);
    if (onCallerAbort) callerSignal.removeEventListener('abort', onCallerAbort);
  }
}

export async function transcribeViaUvtp({
  audioBase64,
  mimeType = 'audio/webm',
  durationSec,
  source = 'assistant',
  vocabPrompt,
  forceHeavy = false,
  maxPollMs = UVTP_MAX_POLL_MS_DEFAULT,
  fetchImpl = defaultUvtpFetch,
  // TICKET-CODEX-3199616574: optional AbortSignal so callers (conversation mode)
  // can cancel the in-flight transcription on session close without leaving
  // stale server work running.
  signal,
} = {}) {
  if (!audioBase64) throw new Error('transcribeViaUvtp: audioBase64 is required');

  // TICKET-CODEX-3199616574: if the caller supplied an AbortSignal (e.g. from
  // finalizeAbortRef in conversation mode), wrap fetchImpl so every request
  // carries it via options.signal. defaultUvtpFetch (TICKET-1031) composes
  // this with its own timeout signal rather than overwriting it, so the
  // caller's abort and the internal timeout both cancel the request.
  const wrappedFetch = signal
    ? (url, opts = {}) => {
        const combined = typeof AbortSignal?.any === 'function'
          ? AbortSignal.any([signal])
          : signal;
        return fetchImpl(url, { ...opts, signal: combined });
      }
    : fetchImpl;

  if (signal?.aborted) throw new DOMException('transcribeViaUvtp cancelled', 'AbortError');

  const t0 = Date.now();
  const postRes = await wrappedFetch(UVTP_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ audioBase64, mimeType, durationSec, source, vocabPrompt, forceHeavy }),
  });
  const post = await postRes.json();

  if (postRes.status === 200 && post.status === 'complete') {
    return {
      text: post.text || '',
      uvtpRunId: post.uvtpRunId,
      path: post.path,
      provider: post.provider,
      fallbackUsed: !!post.fallbackUsed,
      durationMs: typeof post.durationMs === 'number' ? post.durationMs : (Date.now() - t0),
      heavyPathPolled: false,
    };
  }
  if (postRes.status !== 202 || post.status !== 'queued' || !post.uvtpRunId) {
    throw new Error(`UVTP POST returned unexpected ${postRes.status}/${post.status}: ${post.error || JSON.stringify(post).slice(0, 200)}`);
  }

  const runId = post.uvtpRunId;
  const pollIntervalMs = Number(post.pollIntervalMs) || UVTP_POLL_INTERVAL_MS_DEFAULT;
  const deadline = Date.now() + maxPollMs;
  let last = null;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException('transcribeViaUvtp poll cancelled', 'AbortError');
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    const r = await wrappedFetch(`${UVTP_ENDPOINT}?runId=${runId}`);
    last = await r.json();
    if (last.status === 'complete' || last.status === 'failed') break;
  }
  if (!last || last.status !== 'complete') {
    throw new Error(`UVTP heavy path did not complete in ${maxPollMs}ms (last status: ${last?.status || 'unknown'})`);
  }
  return {
    text: last.fullTranscript || '',
    uvtpRunId: runId,
    path: 'heavy',
    provider: last.chunks?.[0]?.provider,
    fallbackUsed: (last.fallbackRate || 0) > 0,
    durationMs: Date.now() - t0,
    heavyPathPolled: true,
  };
}

// ── insertVoiceRecord (Phase 3 BCRUMB-UVTP — transcript-ASAP) ─────────
//
// Client-side helper that decouples transcript-write from classifier-run.
// After UVTP returns the transcript, voiceProcessFn calls this helper to
// (a) INSERT a voice_records row (status='classifying') and (b) INSERT
// a matching voice_transcripts row (raw_transcript=transcript). Returns
// { voice_record_id } so the worker can clear the IDB queue immediately
// and fire-and-forget classifyOnly(voice_record_id) for the metadata pass.
//
// Why both inserts: BreadcrumbCard.jsx:159 reads the visible transcript
// from voice_transcripts.raw_transcript / edited_transcript, NOT from
// voice_records.body — so writing only voice_records would leave the card
// blank for ~6-17s while classifier runs. Mirrors handleCapture
// voice-v2.js:111+141.
//
// status='classifying' is already valid per migration 062's CHECK
// constraint — no migration needed. Matches what voice-classifier.js:588
// sets at pipeline start, so a parallel classifier kicked off via
// classifyOnly will simply overwrite to the same value before flipping
// to 'ready'.
//
// classifier_profile is clamped to DB_ALLOWED_PROFILES = ['crm','project']
// per the voice_records_classifier_profile_check CHECK constraint —
// kept in sync with the server-side resolveClassifierProfile in
// lib/handlers/voice-v2.js. Locked by tests/lib/api/voice-v2-insertVoiceRecord.test.js.
function resolveClassifierProfileClient(contextType, contextHint) {
  if (contextHint === 'pipeline' || contextType === 'deal') return 'crm';
  return 'project';
}

export async function insertVoiceRecord({
  transcript,
  contextType,
  contextId,
  contextHint,
  idempotencyKey,
  fileName,
  language,
  durationSeconds,
  supabaseImpl,
} = {}) {
  if (typeof transcript !== 'string' || transcript.trim() === '') {
    throw new Error('insertVoiceRecord: transcript is required');
  }
  if (!idempotencyKey) {
    throw new Error('insertVoiceRecord: idempotencyKey is required');
  }

  const supabase = supabaseImpl || (await import('../supabase')).supabase;

  // RLS requires user_id = auth.uid() on voice_records (migration 067:394).
  // Resolve from session BEFORE any insert so we fail loud if auth is gone.
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id;
  if (!userId) {
    throw new Error('insertVoiceRecord: no auth session — user must be signed in');
  }

  // Idempotency check — handleCapture voice-v2.js:84-94 parity. Worker
  // retries (e.g. flaky network on the classifyOnly call) re-enter this
  // path; the lookup short-circuits without duplicating voice_records.
  //
  // Bug E (CEO live test 2026-05-05 18:47+02): the prior insertVoiceRecord
  // crashed AFTER voice_records insert but BEFORE voice_transcripts insert
  // (Bug D engine_used CHECK failure). Worker retries hit the early-return
  // and SKIPPED the voice_transcripts insert → permanent stuck state with a
  // voice_records row but no transcript row → BreadcrumbCard renders empty.
  //
  // Partial-failure recovery: when voice_records exists, also check
  // voice_transcripts. If the transcript row is missing, complete the pair
  // before returning so the BreadcrumbCard finally has something to read.
  const existing = await supabase
    .from('voice_records')
    .select('id, status')
    .eq('idempotency_key', idempotencyKey)
    .eq('user_id', userId)
    .maybeSingle();
  if (existing?.data?.id) {
    const existingId = existing.data.id;
    const { data: existingTx } = await supabase
      .from('voice_transcripts')
      .select('id')
      .eq('voice_record_id', existingId)
      .limit(1)
      .maybeSingle();
    if (!existingTx?.id) {
      const { error: recoveryError } = await supabase
        .from('voice_transcripts')
        .insert({
          voice_record_id: existingId,
          raw_transcript: transcript,
          engine_used: 'whisper',
          processing_time_ms: 0,
          chunk_index: 0,
        });
      if (recoveryError) {
        throw new Error(`insertVoiceRecord: partial-recovery voice_transcripts insert failed: ${recoveryError.message}`);
      }
    }
    return { voice_record_id: existingId };
  }

  const projectId = contextType === 'project' ? contextId : null;
  const crmDealId = contextType === 'deal' ? contextId : null;
  const taskId = contextType === 'task' ? contextId : null;
  const bundleId = contextType === 'bundle' ? contextId : null;
  const classifierProfile = resolveClassifierProfileClient(contextType, contextHint);

  // NOTE: voice_records does NOT have a `file_name` column (verified prod schema
  // 2026-05-05). Don't add `file_name` here — it tripped a Supabase schema cache
  // miss → "Could not find the 'file_name' column" → worker retry loop. The
  // `fileName` parameter is kept on the function signature for caller-shape
  // stability and to mirror handleCapture, but is intentionally not persisted.
  // eslint-disable-next-line no-unused-vars -- accept-and-ignore for caller compat
  const _fileNameDiscarded = fileName;
  const recordRow = {
    user_id: userId,
    note_type: 'voice_note',
    intent_type: contextHint || 'breadcrumb',
    status: 'classifying',
    language: language || 'en',
    idempotency_key: idempotencyKey,
    project_id: projectId,
    crm_deal_id: crmDealId,
    task_id: taskId,
    bundle_id: bundleId,
    classifier_profile: classifierProfile,
    duration_seconds: durationSeconds || null,
    stt_engine: 'uvtp',
  };

  const { data: record, error: recordError } = await supabase
    .from('voice_records')
    .insert(recordRow)
    .select()
    .single();

  if (recordError || !record) {
    const msg = recordError?.message || 'no row returned';
    throw new Error(`insertVoiceRecord: voice_records insert failed: ${msg}`);
  }

  // voice_transcripts is the read source for BreadcrumbCard. Failure here
  // means the user sees a transcript-less card; we throw so voiceProcessFn
  // bubbles the error to the worker, which retries with the same
  // idempotency_key (which now hits the idempotent return above for the
  // already-inserted voice_records, and re-attempts the voice_transcripts
  // insert — eventually consistent).
  // engine_used is constrained by voice_transcripts_engine_used_check to
  // ('gemini' | 'whisper' | 'whisper-1') — see prod constraint dump
  // 2026-05-05. UVTP fast path invokes OpenAI Whisper server-side so
  // 'whisper' is accurate. Routing-layer attribution lives on
  // voice_records.stt_engine='uvtp' (different column, no constraint).
  // Don't set 'uvtp' here — it trips the CHECK and the worker retry-loops.
  const { error: transcriptError } = await supabase
    .from('voice_transcripts')
    .insert({
      voice_record_id: record.id,
      raw_transcript: transcript,
      engine_used: 'whisper',
      processing_time_ms: 0,
      chunk_index: 0,
    });

  if (transcriptError) {
    throw new Error(`insertVoiceRecord: voice_transcripts insert failed: ${transcriptError.message}`);
  }

  return { voice_record_id: record.id };
}

// ── classifyOnly (Phase 3 BCRUMB-UVTP — fire-and-forget classifier kick) ──
//
// After insertVoiceRecord lands the transcript, voiceProcessFn fires
// classifyOnly(voice_record_id) without awaiting — the breadcrumb card
// already cleared from "Processing…" with the transcript visible. The
// server-side voice.classify_only handler reads the stored row, runs the
// classifier (~6-17s), UPDATEs voice_records with intent_type / headline /
// summary / status='ready', and the realtime UPDATE re-renders the card
// with the metadata in place.
//
// This helper is the thin transport: POST {action, voice_record_id} to
// /api/ai. Returns the apiFetch shape directly. Does NOT throw on server
// error (the caller fired-and-forgot — the server logs the failure to
// app_error_logs via action_intent + action_result, and the user sees a
// transcript-only card which still has value).
//
// Locked by tests/lib/api/voice-v2-classifyOnly.test.js.
// TICKET-1032: no bounded timeout -- a hung network request left this
// fire-and-forget classifier kick pending forever (classifyOnly's own
// try/catch already turns a rejected fetch into a normal error shape, so a
// 25s abort surfaces exactly like any other classifier failure).
const CLASSIFY_ONLY_TIMEOUT_MS = 25000; // classifier takes ~6-17s (see above); 15s cut the slow tail

async function defaultClassifyOnlyFetch(url, options = {}) {
  const { getAuthHeaders } = await import('./client');
  const auth = await getAuthHeaders();
  const controller = new AbortController();
  const timeoutMs = typeof options.timeout === 'number' ? options.timeout : CLASSIFY_ONLY_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: { ...(options.headers || {}), ...auth },
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function classifyOnly(voiceRecordId, { fetchImpl = defaultClassifyOnlyFetch } = {}) {
  if (!voiceRecordId || typeof voiceRecordId !== 'string') {
    throw new Error('classifyOnly: voice_record_id is required');
  }
  const url = `${API_BASE}/ai`;
  try {
    const res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action: 'voice.classify_only', voice_record_id: voiceRecordId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: body.error || `classifier failed (${res.status})` };
    }
    return body;
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function captureVoice({ audio, transcript, contextType, contextId, contextHint, language, idempotencyKey, fileName, mimeType }) {
  // Voice capture runs Whisper + classifier pipeline server-side (often 15-40s).
  // Override default 15s apiFetch timeout with 45s so normal recordings complete
  // synchronously and the client receives the final voice_record_id + transcript.
  // TICKET-1045: carries idempotency_key -- don't let a client.js retry fire
  // a second metered capture (STT + classifier) for the same recording.
  return apiFetch(VOICE, {
    method: 'POST',
    timeout: 45000,
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.capture',
      audio: audio || undefined,
      transcript: transcript || undefined,
      context_type: contextType,
      context_id: contextId,
      context_hint: contextHint || undefined,
      language,
      idempotency_key: idempotencyKey,
      stt_engine: transcript ? 'text_import' : getSttProviderName(),
      stt_provider: transcript ? 'text_import' : getSttProviderName(),
      file_name: fileName || undefined,
      // TICKET-1440: the recorded container (an iPhone records audio/mp4); the server defaults to webm.
      mime_type: mimeType || undefined,
    }),
  });
}

// ── Process (retry transcription for uploaded/errored records) ───────

export async function processVoice(voiceRecordId) {
  // Voice processing can take 15-20s (Whisper + classification).
  // Use a longer timeout than the default 15s.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 30000); // 30s
  try {
    const { getAuthHeaders } = await import('./client');
    const auth = await getAuthHeaders();
    const res = await fetch(VOICE, {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify({ action: 'voice.process', voice_record_id: voiceRecordId }),
    });
    clearTimeout(timer);
    return await res.json();
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      return { success: false, error: 'Processing timed out — audio is saved, try again shortly' };
    }
    throw err;
  }
}

// ── Actions ─────────────────────────────────────────────────────────

export async function updateAction({ actionId, operation, edits }) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.update_action',
      action_id: actionId,
      operation,
      edits,
    }),
  });
}

// ── Execute action (convert derived_action → real entity) ──────────

export async function executeVoiceAction(actionId, overrides = {}) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'action.execute', action_id: actionId, overrides }),
  });
}

// ── Archive voice note ──────────────────────────────────────────────

export async function archiveVoiceNote(voiceRecordId) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.archive_voice_note', voice_record_id: voiceRecordId }),
  });
}

// ── Delete voice note ───────────────────────────────────────────────

export async function deleteVoiceNote(voiceRecordId) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.delete_voice_note', voice_record_id: voiceRecordId }),
  });
}

// ── Queries ─────────────────────────────────────────────────────────

export async function fetchVoiceNotes(filters = {}) {
  const params = new URLSearchParams({ action: 'voice.list' });
  if (filters.contextType) params.set('context_type', filters.contextType);
  if (filters.contextId) params.set('context_id', filters.contextId);
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  const data = await apiFetch(`${VOICE}?${params}`);
  return data?.voice_notes || [];
}

export async function fetchVoiceDetail(id) {
  const params = new URLSearchParams({ action: 'voice.detail', id });
  return apiFetch(`${VOICE}?${params}`);
}

// ── New project from a voice note (TICKET-1440 S4) ──────────────────
// Raw fetch, not apiFetch: apiFetch drops the error body on a non-2xx POST, and the
// review screen needs the 409 `match` + `step`. No retry: create_all writes.
async function postAiAction(body) {
  const { getAuthHeaders } = await import('./client');
  const auth = await getAuthHeaders();
  const res = await fetch(VOICE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(body),
  });
  // A non-JSON body is still a result: the handler emits its own step, the caller sees status.
  const data = await res.json().catch(() => { /* spine-emit-ok: non-JSON body, status still returned */ return {}; });
  return { ...data, status: res.status, ok: res.ok && data.ok !== false };
}

/** voice.new_project.preview -> { ok, status, company, contacts, vessel, open_deal, bundles } */
export function previewNewProject(voiceRecordId) {
  return postAiAction({ action: 'voice.new_project.preview', voice_record_id: voiceRecordId });
}

/** voice.new_project.create_all -> { ok, status, deal_id, ... } | { ok:false, status:409, step, match } */
export function createAllNewProject(voiceRecordId, choices) {
  return postAiAction({ action: 'voice.new_project.create_all', voice_record_id: voiceRecordId, choices });
}

// ── Voice Uploads (large file intake from iPhone) ───────────────────

// Files >= this route through the RESUMABLE (tus) endpoint. Supabase's standard
// upload is capped by the project "Upload file size limit" (default 50 MB) —
// CEO 2026-05-31: a 58 MB file → "The object exceeded the maximum allowed size".
// 40 MB threshold gives margin under the 50 MB cap; small files keep the proven
// standard path.
const TUS_THRESHOLD_BYTES = 40 * 1024 * 1024;
const TUS_CHUNK_BYTES = 6 * 1024 * 1024; // Supabase resumable requires 6 MB chunks

// MinIO direct-upload flag (CEO 2026-05-31). OFF until the box env is wired +
// CEO verifies (set VITE_MINIO_UPLOAD=1). When ON, files >= TUS_THRESHOLD_BYTES
// upload straight to MinIO on the Hetzner box (bypassing the free-plan Supabase
// 50 MB cap that 413s both standard AND tus); the tus path stays as the fallback
// when the flag is off.
const MINIO_UPLOAD_ENABLED = (() => {
  try { const v = import.meta.env?.VITE_MINIO_UPLOAD; return v === '1' || v === 'true'; }
  catch { return false; }
})();

// Resumable S3-multipart upload flag (TICKET-MINIO-RESUMABLE Slice 2, CEO 2026-06-01).
// OFF until the box CORS is verified (set VITE_MINIO_RESUMABLE=1). When ON *and*
// MinIO upload is enabled, large files (>= TUS_THRESHOLD_BYTES) upload as
// multipart (split into parts, resume from the last completed part on reopen)
// instead of a single presigned PUT. Multipart needs MinIO/nginx to CORS-expose
// the ETag response header; until that's confirmed live, the proven single-PUT
// path (uploadViaPresigned, validated on a real 188 MB file) stays the default.
const MINIO_RESUMABLE_ENABLED = (() => {
  try { const v = import.meta.env?.VITE_MINIO_RESUMABLE; return v === '1' || v === 'true'; }
  catch { return false; }
})();

/**
 * Resumable (tus) upload directly to Supabase Storage — bypasses the standard
 * upload size cap AND Vercel's 4.5 MB function body limit (client → Supabase),
 * streams 6 MB chunks up to the bucket's file_size_limit (500 MB), and survives
 * iOS Safari backgrounding. `tusImpl` (the Upload ctor) is injectable for tests.
 */
async function tusUploadToStorage({ supabase, file, storagePath, contentType, tusImpl, onProgress, upsert = false }) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error('no auth session for resumable upload');
  const UploadCtor = tusImpl || (await import('tus-js-client')).Upload;
  let baseUrl;
  let anonKey;
  try {
    baseUrl = import.meta.env?.VITE_SUPABASE_URL;
    anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  } catch {
    // import.meta unavailable in some runtimes — endpoint falls back below
  }
  const endpoint = `${baseUrl}/storage/v1/upload/resumable`;
  await new Promise((resolve, reject) => {
    const upload = new UploadCtor(file, {
      endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: { authorization: `Bearer ${token}`, apikey: anonKey, 'x-upsert': upsert ? 'true' : 'false' },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      chunkSize: TUS_CHUNK_BYTES,
      metadata: {
        bucketName: 'voice-uploads',
        objectName: storagePath,
        contentType: contentType || 'application/octet-stream',
        cacheControl: '3600',
      },
      onError: (err) => reject(err instanceof Error ? err : new Error(String(err?.message || err))),
      onProgress: (sent, total) => { if (onProgress) onProgress(sent, total); },
      onSuccess: () => resolve(),
    });
    upload.start();
  });
}

/**
 * POST voice.register_upload, then fire-and-forget voice.process_upload. Shared by
 * uploadVoiceFile (live path) and the resume-on-reopen finalizer (resumeUploadsOnStart)
 * so the register/process mechanics live in one place. The CALLER builds registerBody
 * (the scope/file shape) — kept at the call site so the optimistic-breadcrumb sentinel's
 * source assertions on registerBody stay valid — and owns the surrounding obs context.
 * @returns {Promise<{upload_id?:string}>} the register response (forwarded to the caller)
 */
export async function postRegisterAndProcess({ registerBody, fetchImpl = apiFetch, obsBase = {} }) {
  let result;
  try {
    result = await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(registerBody) });
  } catch (err) {
    logVoiceEvent('voice.upload.register.error', 'error', err.message, { ...obsBase, error_message: err.message });
    throw err;
  }
  logVoiceEvent('voice.upload.register.ok', 'ok', 'Registered upload', { ...obsBase, upload_id: result?.upload_id ?? null });
  // Kick off transcription (fire and forget — client polls for status).
  if (result?.upload_id) {
    fetchImpl(VOICE, {
      method: 'POST',
      body: JSON.stringify({ action: 'voice.process_upload', upload_id: result.upload_id }),
    }).catch((err) => {
      // TICKET-1030: was a bare console.warn -- invisible to the spine, so a
      // broken transcription kickoff (metered STT never fires) left no
      // app_error_logs row to alert on. Route through the same voice spine
      // logger used for the register success/error paths above.
      logVoiceEvent('voice.upload.process_upload.error', 'error', err.message, {
        ...obsBase, upload_id: result.upload_id, error_message: err.message,
      });
    });
  }
  return result;
}

export async function uploadVoiceFile(scopeId, file, contextType = 'project', opts = {}) {
  // Bug 1 (CEO 2026-05-07): accept idempotency_key (recording_id) for the
  // optimistic-breadcrumb reconciliation contract. Threaded through to
  // voice_uploads.idempotency_key by the voice.register_upload handler, then
  // forwarded by kickClassification into voice_records.idempotency_key —
  // closing the loop with the optimistic row inserted by useVoiceBreadcrumbs
  // on the voice:record_stop event.
  // TICKET-064 (CEO live 2026-05-07): accept duration_sec so the History
  // card's compact row renders a real duration instead of a "—" placeholder.
  // Server-side voice.register_upload handler already writes the column
  // (api/ai.js:2167) — the gap was that nothing client-side sent the value.
  //
  // CEO 2026-05-31: first param is the SCOPE id (project id for project uploads,
  // deal id for deal uploads). Large deal uploads now route here instead of
  // base64. The register body carries scope_type/scope_id so the server writes
  // the right voice_uploads FK (deal_id for deal, project_id for project).
  const {
    idempotency_key, duration_sec,
    // Injectable seams (mirror insertVoiceRecord) so the previously-DARK client
    // upload path is testable. Default to the real client + apiFetch in prod.
    supabaseImpl, apiFetchImpl, uploadTimeoutMs,
    // onProgress(bytesSent, bytesTotal) — surfaced to the caller so the
    // direct-upload UI (VoiceFileUploadBtn) can show a real % instead of a
    // blind spinner. Only fires on the large-file (MinIO) transports.
    onProgress: callerOnProgress,
  } = opts || {};
  const fetchImpl = apiFetchImpl || apiFetch;
  const traceId = idempotency_key || newTraceId();
  const obsBase = { trace_id: traceId, scope_type: contextType, scope_id: scopeId };

  // FQ-25 Option B: when a transport PUT fails below, the upload is stranded with
  // only a tray error — no DB row, so it never shows in the user's History. This
  // best-effort helper registers a TERMINAL voice_uploads row (status='error' via
  // the failed_upload flag) so the failure is visible + queryable. It NEVER masks
  // the original upload error: it's wrapped in its own try/catch and the caller
  // still throws afterward. It does NOT kick processing (no postRegisterAndProcess).
  async function recordFailedUpload(uploadErr, transport) {
    try {
      // Build the failed-register body explicitly at the call site (mirrors the
      // happy-path registerBody ~737 so the shape stays auditable). file_path is a
      // sentinel — the bytes never reached storage, so there is nothing to fetch.
      const failedRegisterBody = {
        action: 'voice.register_upload',
        failed_upload: true,
        error_message: uploadErr?.message || `upload transport (${transport}) failed`,
        scope_type: contextType,
        scope_id: scopeId,
        file_path: `failed/${transport}/${Date.now()}`,
        file_name: file.name,
        file_size_bytes: file.size,
        mime_type: file.type,
        context_type: contextType,
      };
      if (contextType === 'project') failedRegisterBody.project_id = scopeId;
      if (idempotency_key) failedRegisterBody.idempotency_key = idempotency_key;
      await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(failedRegisterBody) });
      logVoiceEvent('voice.upload.failed_recorded', 'error', 'Recorded terminal failed-upload row', {
        ...obsBase, transport, bytes: file.size, error_message: uploadErr?.message,
      });
    } catch (recErr) {
      // spine-emit-ok: best-effort failed-upload record; original error rethrown
      logVoiceEvent('voice.upload.failed_record.error', 'error', recErr?.message, {
        ...obsBase, transport, error_message: recErr?.message,
      });
    }
  }

  // 0-byte guard (OBSERVABILITY audit CEO 2026-05-31): an empty File means the
  // bytes aren't actually available (e.g. an un-downloaded iCloud file iOS
  // reports a size for but can't read). Uploading it stores a 0-byte object that
  // fails downstream with a misleading "Whisper: Invalid file format". Fail
  // HONESTLY at the client AND emit so the spine sees it.
  if (!file || !(file.size > 0)) {
    logVoiceEvent('voice.upload.empty', 'error', 'File is 0 bytes — not uploading', {
      ...obsBase, bytes: file?.size ?? 0, file_name: file?.name, error_message: 'empty file (0 bytes)',
    });
    throw new Error('uploadVoiceFile: file is empty (0 bytes) — likely an un-downloaded iCloud file. Open it in Files to download it, then retry.');
  }

  const supabase = supabaseImpl || (await import('../../lib/supabase')).supabase;
  // TICKET-837: key the object on the recording's idempotency_key so a retry
  // of the SAME failed upload overwrites its own object instead of adding a
  // new copy (measured: one 31MB recording stored 3x, another 3x23MB, six
  // 0-byte copies of a third — 165MB of a 225MB bucket, all from retries).
  // No idempotency_key on this call path → fall back to the old Date.now()
  // key (unique-per-attempt, upsert:false) rather than inventing a shared one.
  const hasStableKey = !!idempotency_key;
  const uploadKey = hasStableKey ? idempotency_key.replace(/[^a-zA-Z0-9._-]/g, '_') : Date.now();
  const storagePath = `${scopeId}/${uploadKey}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

  // 1. Upload to Supabase Storage. Large files (>=40MB) use the RESUMABLE (tus)
  //    endpoint — the standard upload is capped (CEO 2026-05-31: 58MB →
  //    "exceeded the maximum allowed size"); resumable streams 6MB chunks up to
  //    the bucket limit, client→Supabase. Small files keep the proven standard
  //    path, with a hang TIMEOUT so a stuck upload can't jam the single-threaded
  //    voice queue worker forever (CEO 2026-05-31: 63KB stuck "Uploading" 4 min).
  const t0 = Date.now();
  const useMinio = MINIO_UPLOAD_ENABLED && file.size >= TUS_THRESHOLD_BYTES;
  const useResumable = useMinio && MINIO_RESUMABLE_ENABLED;
  const useTus = !useMinio && file.size >= TUS_THRESHOLD_BYTES;
  const transport = useMinio ? (useResumable ? 'minio-multipart' : 'minio') : (useTus ? 'tus' : 'standard');
  // Resume-on-reopen key (Slice 3b) — only set on the resumable path. The recording_id
  // (idempotency_key) is the natural stable key; fall back to the trace id.
  const resumeKey = useResumable ? (idempotency_key || traceId) : null;
  // uploadPath + backend feed voice.register_upload below: MinIO writes the
  // object key with storage_backend='hetzner' (the worker pulls from MinIO);
  // the Supabase paths keep storage_backend='supabase' + the storagePath.
  let uploadPath = storagePath;
  let backend = 'supabase';
  logVoiceEvent('voice.upload.storage.start', 'start', `Uploading ${file.size} bytes (${transport})`, {
    ...obsBase, bytes: file.size, mime: file.type, transport,
  });
  if (useMinio) {
    try {
      // Persist resume state BEFORE the first part is PUT, so an interruption
      // (iOS backgrounds Safari, app close) can resume from the last completed
      // part on reopen. Best-effort — the upload still proceeds if IDB write fails.
      if (resumeKey) {
        await saveUpload({
          key: resumeKey, scopeType: contextType, scopeId,
          fileName: file.name, mimeType: file.type, fileSize: file.size,
          fileBlob: file, session: null,
        }).catch(() => { /* spine-emit-ok: best-effort resume persist; upload proceeds */ });
      }
      // Resumable multipart when the flag is on (survives interruptions); else the
      // proven single presigned PUT. Both return { object_key, storage_backend }.
      const r = useResumable
        ? await uploadResumable({
            file, scopeType: contextType, scopeId,
            apiFetchImpl: fetchImpl, xhrImpl: opts.xhrImpl,
            onState: (session) => { if (resumeKey) patchUpload(resumeKey, { session }).catch(() => { /* spine-emit-ok: best-effort progress persist */ }); },
            onProgress: (sent, total) => {
              if (callerOnProgress) callerOnProgress(sent, total);
              logVoiceEvent('voice.upload.minio.progress', 'ok', `${sent}/${total}`, {
                ...obsBase, bytes_uploaded: sent, bytes: total, duration_ms: Date.now() - t0, transport: 'minio-multipart',
              });
            },
          })
        : await uploadViaPresigned({
            file, scopeType: contextType, scopeId, idempotencyKey: idempotency_key,
            apiFetchImpl: fetchImpl, xhrImpl: opts.xhrImpl,
            onProgress: (sent, total) => {
              if (callerOnProgress) callerOnProgress(sent, total);
              logVoiceEvent('voice.upload.minio.progress', 'ok', `${sent}/${total}`, {
                ...obsBase, bytes_uploaded: sent, bytes: total, duration_ms: Date.now() - t0,
              });
            },
          });
      uploadPath = r.object_key;
      backend = r.storage_backend || 'hetzner';
    } catch (err) {
      logVoiceEvent('voice.upload.minio.error', 'error', err.message, {
        ...obsBase, transport: 'minio', duration_ms: Date.now() - t0, bytes: file.size, error_message: err.message,
      });
      // FQ-25: record the strand as a terminal row before rethrowing (best-effort).
      await recordFailedUpload(err, 'minio');
      throw new Error('Upload failed: ' + err.message);
    }
    logVoiceEvent('voice.upload.minio.ok', 'ok', 'Stored (minio)', {
      ...obsBase, transport: 'minio', duration_ms: Date.now() - t0, bytes: file.size,
    });
  } else if (useTus) {
    try {
      await tusUploadToStorage({
        supabase, file, storagePath, contentType: file.type, tusImpl: opts.tusImpl, upsert: hasStableKey,
        onProgress: (sent, total) => {
          logVoiceEvent('voice.upload.storage.progress', 'ok', `${sent}/${total}`, {
            ...obsBase, bytes_uploaded: sent, bytes: total, duration_ms: Date.now() - t0,
          });
        },
      });
    } catch (err) {
      logVoiceEvent('voice.upload.storage.error', 'error', err.message, {
        ...obsBase, transport: 'tus', duration_ms: Date.now() - t0, bytes: file.size, error_message: err.message,
      });
      // FQ-25: record the strand as a terminal row before rethrowing (best-effort).
      await recordFailedUpload(err, 'tus');
      throw new Error('Upload failed: ' + err.message);
    }
    logVoiceEvent('voice.upload.storage.ok', 'ok', 'Stored (resumable)', {
      ...obsBase, transport: 'tus', duration_ms: Date.now() - t0, bytes: file.size,
    });
  } else {
    const timeoutMs = typeof uploadTimeoutMs === 'number' ? uploadTimeoutMs : 180000;
    let storageError = null;
    let timer;
    try {
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`storage upload timed out after ${timeoutMs}ms`)), timeoutMs);
      });
      // TICKET-658: ArrayBuffer, not File — storage-js wraps a Blob/File body
      // in FormData with an empty field name, which WebKit intermittently
      // mis-serializes on larger payloads ("No content provided" server-side).
      const res = await Promise.race([
        supabase.storage.from('voice-uploads').upload(storagePath, await file.arrayBuffer(), { contentType: file.type, upsert: hasStableKey }),
        timeout,
      ]);
      storageError = res?.error || null;
    } catch (err) {
      logVoiceEvent('voice.upload.storage.error', 'error', err.message, {
        ...obsBase, transport: 'standard', duration_ms: Date.now() - t0, bytes: file.size, error_message: err.message,
      });
      // FQ-25: record the strand as a terminal row before rethrowing (best-effort).
      await recordFailedUpload(err, 'standard');
      throw new Error('Upload failed: ' + err.message);
    } finally {
      clearTimeout(timer);
    }
    if (storageError) {
      logVoiceEvent('voice.upload.storage.error', 'error', storageError.message, {
        ...obsBase, transport: 'standard', duration_ms: Date.now() - t0, bytes: file.size, error_message: storageError.message,
      });
      // FQ-25: record the strand as a terminal row before rethrowing (best-effort).
      await recordFailedUpload(storageError, 'standard');
      throw new Error('Upload failed: ' + storageError.message);
    }
    logVoiceEvent('voice.upload.storage.ok', 'ok', 'Stored', {
      ...obsBase, transport: 'standard', duration_ms: Date.now() - t0, bytes: file.size,
    });
  }

  // 2. Register in database with scope + context_type + idempotency_key (Bug 1)
  const registerBody = {
    action: 'voice.register_upload',
    scope_type: contextType,
    scope_id: scopeId,
    file_path: uploadPath,
    file_name: file.name,
    file_size_bytes: file.size,
    mime_type: file.type,
    context_type: contextType,
    storage_backend: backend,
  };
  // Back-compat: project scope keeps sending project_id verbatim so the existing
  // server path + sentinels are unchanged. Non-project scopes (deal) are mapped
  // server-side from scope_type/scope_id to the right FK column.
  if (contextType === 'project') registerBody.project_id = scopeId;
  if (idempotency_key) registerBody.idempotency_key = idempotency_key;
  if (typeof duration_sec === 'number') registerBody.duration_sec = duration_sec;

  // 2+3. Register + kick transcription via the shared helper (also used by the
  // resume-on-reopen finalizer). registerBody is built ABOVE (stays at this call site).
  const result = await postRegisterAndProcess({ registerBody, fetchImpl, obsBase });

  // Resume-on-reopen: the upload finished + registered, so drop the persisted
  // resume record (Slice 3b). Best-effort — a leftover record is harmless (the
  // resumer re-PUTs idempotently), but cleaning up frees the stored file bytes.
  if (resumeKey) deleteUpload(resumeKey).catch(() => { /* spine-emit-ok: IDB cleanup is best-effort */ });

  return result;
}

// ── Project Activity Feed ─────────────────────────────────────────────

export async function fetchProjectActivity(projectId, projectName, limit = 50) {
  const params = new URLSearchParams({
    action: 'brain.activity',
    project_id: projectId,
    project_name: projectName || '',
    limit: String(limit),
  });
  const data = await apiFetch(`${API_BASE}/ai?${params}`);
  return data?.items || [];
}

export async function fetchUploadProgress(uploadId) {
  const params = new URLSearchParams({ action: 'voice.upload_progress', upload_id: uploadId });
  return apiFetch(`${VOICE}?${params}`);
}

export async function fetchVoiceUploads(projectId, bundleId) {
  const params = new URLSearchParams({ action: 'voice.list_uploads', project_id: projectId });
  if (bundleId) params.set('bundle_id', bundleId);
  const data = await apiFetch(`${VOICE}?${params}`);
  return data?.uploads || [];
}

// TICKET-065 — realtime subscription on voice_uploads filtered by project_id.
// Backbone for TICKET-063's ready-toast: when the cron drainer / classifier
// flips status to 'ready', the UPDATE event fires here, HistoryTab updates
// its uploads state, and useVoiceUploadReadyToast (transition detector)
// fires the toast instantly — no polling delay, works even when the user
// is on a different tab in the same project.
//
// Random suffix per call avoids the TICKET-025 channel-collision class
// (multiple consumers ↔ same name = postgres_changes-after-subscribe crash).
//
// Returns the channel object so callers can unsubscribe via
// supabase.removeChannel(channel) on unmount. Returns null when projectId
// is missing OR onChange is not a function (defensive — no channel leaks).
export function subscribeVoiceUploads(projectId, onChange, opts = {}) {
  if (!projectId) return null;
  if (typeof onChange !== 'function') return null;

  const supabaseImpl = opts.supabaseImpl || defaultSupabase;
  const suffix = Math.random().toString(36).slice(2, 10);

  const emit = (type, payload) => {
    const row = payload?.new;
    if (!row) return; // tolerate handshake / error payloads with no .new
    onChange({ type, row });
  };

  const channel = supabaseImpl
    .channel(`voice_uploads:${projectId}:${suffix}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'voice_uploads',
        filter: `project_id=eq.${projectId}`,
      },
      (payload) => emit('INSERT', payload),
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'voice_uploads',
        filter: `project_id=eq.${projectId}`,
      },
      (payload) => emit('UPDATE', payload),
    )
    .subscribe();

  return channel;
}

// Phase 1A (CEO 2026-05-04) — soft-delete a History entry. 403 unless
// caller is admin/ceo/pmo. Idempotent.
export async function archiveVoiceUpload(uploadId) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.archive_upload', upload_id: uploadId }),
  });
}

// Phase 2B (CEO 2026-05-04) — fetch the additive vessel/project brain rollup
// computed server-side from all brain_summary_json blobs.
export async function fetchProjectBrainSummary(projectId) {
  const params = new URLSearchParams({ action: 'voice.project_brain_summary', project_id: projectId });
  const data = await apiFetch(`${VOICE}?${params}`);
  return data?.rollup || null;
}

// Phase 1B (CEO 2026-05-04) — add a manual markdown note to a project's
// History timeline. Renders alongside voice entries.
export async function addHistoryNote({ projectId, bundleId, body, title }) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.add_history_note',
      project_id: projectId,
      bundle_id: bundleId || undefined,
      body,
      title,
    }),
  });
}

// ── Brain (project knowledge base) ─────────────────────────────────────

export async function fetchBrainItems(projectId) {
  const params = new URLSearchParams({ action: 'voice.brain_items', project_id: projectId });
  return apiFetch(`${VOICE}?${params}`);
}

export async function addManualNote({ projectId, actionType, title, description }) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.add_manual_note',
      project_id: projectId,
      action_type: actionType,
      title,
      description,
    }),
  });
}

// ── Classifier Profiles ─────────────────────────────────────────────

export async function saveProfileVersion({ classifierProfileId, systemPrompt, outputSchema, changelog }) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({
      action: 'voice.save_profile_version',
      classifier_profile_id: classifierProfileId,
      system_prompt: systemPrompt,
      output_schema: outputSchema,
      changelog,
    }),
  });
}

export async function fetchProfileVersions(classifierProfileId) {
  const params = new URLSearchParams({ action: 'voice.profile_versions', classifier_profile_id: classifierProfileId });
  const data = await apiFetch(`${VOICE}?${params}`);
  return data?.versions || [];
}

// ── Vocabulary ──────────────────────────────────────────────────────

export async function fetchVocabulary() {
  const data = await apiFetch(`${VOICE}?action=vocabulary`);
  return data?.vocabulary || [];
}

export async function updateVocabulary(payload) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.vocabulary', ...payload }),
  });
}

// ── Chunk Upload ────────────────────────────────────────────────────

export async function uploadChunk({ recordingId, chunkIndex, audio, mimeType, isFinalChunk, contextType, contextId, language, sttProvider }) {
  // TICKET-1045: recording_id is the server-side dedupe key
  // (findOrCreateVoiceRecord) -- don't let a client.js retry double-store
  // the chunk's metered upload path.
  return apiFetch(VOICE, {
    method: 'POST',
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.upload_chunk',
      recording_id: recordingId,
      chunk_index: chunkIndex,
      audio,
      mime_type: mimeType || 'audio/webm;codecs=opus',
      is_final_chunk: isFinalChunk || false,
      context_type: contextType,
      context_id: contextId,
      language,
      stt_provider: sttProvider || getSttProviderName(),
    }),
  });
}

export async function finalizeRecording(recordingId, { sttProvider } = {}) {
  // TICKET-1045: recording_id keys the finalize pipeline (stitch + classify)
  // -- don't let a client.js retry re-run it.
  return apiFetch(VOICE, {
    method: 'POST',
    noRetry: true,
    body: JSON.stringify({
      action: 'voice.finalize_recording',
      recording_id: recordingId,
      stt_provider: sttProvider || getSttProviderName(),
    }),
  });
}

// ── Phase 2.5 (TICKET-059) — pre-fill intake metadata flow. ─────────────────
// Server suggests vessel/deal/product hints from filename + transcript head;
// CEO confirms in the modal; extraction is gated until confirmed_metadata is set.

export async function suggestVoiceMetadata(uploadId) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.suggestMetadata', upload_id: uploadId }),
  });
}

export async function confirmVoiceMetadata(uploadId, metadata) {
  return apiFetch(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.confirmMetadata', upload_id: uploadId, metadata }),
  });
}

// ── Health ──────────────────────────────────────────────────────────

export async function fetchVoiceHealth() {
  return apiFetch(`${VOICE}?action=voice.health`);
}
